"use client"

import * as React from "react"
import { RelativeDatePreset } from "@ibada/shared-types/common"
import {
  ReportChartType,
  ReportDatasetKind,
  ReportFilterOperator,
  ReportScope,
  ReportTimeGrain,
  ReportValueType,
  ReportViewType,
  ReportVisibility,
  RelationshipUsage,
  type ReportDefinition,
  type ReportEntityRegistry,
  type ReportFilterClause,
  type ReportFilter,
  type ReportRelationship,
  type ReportSort,
} from "@ibada/shared-types/reporting"

import type { ReportsRouteScope, ReportingSavedReportListResult } from "@/features/reports/types/reports-types"
import type {
  ReportBuilderSelection,
  ReportFilterDraft,
  ReportMeasureFilterDraft,
} from "@/features/reports/types/reports-builder-types"

type RelationshipFieldGroup = {
  relationship: ReportRelationship
  dimensions: ReportEntityRegistry["dimensions"]
}

function newDraftId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
}

function createFilterDraft(filter?: ReportFilter): ReportFilterDraft {
  return {
    id: newDraftId(),
    field: filter?.id ?? "",
    operator: filter?.operators[0] ?? ReportFilterOperator.EQUALS,
    rawValue: "",
  }
}

/** Comparative operators offered for a measure (HAVING) filter. */
export const MEASURE_FILTER_OPERATORS: ReportFilterOperator[] = [
  ReportFilterOperator.GT,
  ReportFilterOperator.GTE,
  ReportFilterOperator.LT,
  ReportFilterOperator.LTE,
  ReportFilterOperator.EQUALS,
  ReportFilterOperator.NOT_EQUALS,
]

/**
 * When a date dimension first becomes a group-by / chart x-axis, seed a visible, editable "last 12 months" filter
 * (unless the user already has a filter on that date field). Keeps ad-hoc time reports fast + non-empty by default,
 * while staying fully removable/changeable in the filter UI — we never silently constrain the data.
 */
function seedDateWindowFilter(
  filters: ReportFilterDraft[],
  entity: ReportEntityRegistry | null,
  dateDimensionId: string,
): ReportFilterDraft[] {
  if (!entity) {
    return filters
  }
  const matchingFilter = entity.filters.find((filter) => filter.id === dateDimensionId)
  if (!matchingFilter || !matchingFilter.operators.includes(ReportFilterOperator.RELATIVE_DATE)) {
    return filters
  }
  if (filters.some((filter) => filter.field === dateDimensionId)) {
    return filters
  }
  return [
    ...filters,
    {
      id: newDraftId(),
      field: dateDimensionId,
      operator: ReportFilterOperator.RELATIVE_DATE,
      rawValue: RelativeDatePreset.LAST_365_DAYS,
    },
  ]
}

function createMeasureFilterDraft(measureId?: string): ReportMeasureFilterDraft {
  return {
    id: newDraftId(),
    measure: measureId ?? "",
    operator: ReportFilterOperator.GT,
    rawValue: "",
  }
}

function getDefaultDatasetKind(entity: ReportEntityRegistry) {
  if (entity.capabilities.datasetKinds.includes(ReportDatasetKind.DETAIL)) {
    return ReportDatasetKind.DETAIL
  }

  return entity.capabilities.datasetKinds[0] ?? ReportDatasetKind.DETAIL
}

function getDefaultViewType(entity: ReportEntityRegistry, datasetKind: ReportDatasetKind) {
  if (datasetKind === ReportDatasetKind.DETAIL && entity.capabilities.views.includes(ReportViewType.TABLE)) {
    return ReportViewType.TABLE
  }

  if (datasetKind === ReportDatasetKind.GROUPED) {
    if (entity.capabilities.views.includes(ReportViewType.GROUPED_TABLE)) {
      return ReportViewType.GROUPED_TABLE
    }
    return ReportViewType.TABLE
  }

  if (entity.capabilities.views.includes(ReportViewType.SUMMARY)) {
    return ReportViewType.SUMMARY
  }

  return entity.capabilities.views[0] ?? ReportViewType.TABLE
}

/** The dataset kind a chosen view implies — so the user picks one "view" and we keep the dataset valid. */
function datasetKindForView(viewType: ReportViewType): ReportDatasetKind {
  // Charts are a grouped dataset plotted by a dimension x-axis (e.g. "income by month").
  if (
    viewType === ReportViewType.GROUPED_TABLE ||
    viewType === ReportViewType.PIVOT ||
    viewType === ReportViewType.CHART
  ) {
    return ReportDatasetKind.GROUPED
  }
  if (viewType === ReportViewType.SUMMARY) {
    return ReportDatasetKind.SUMMARY
  }
  return ReportDatasetKind.DETAIL
}

/** A sensible chart x-axis: prefer a time-bucketable date dimension (a trend), else a categorical field. */
function getDefaultChartXAxis(entity: ReportEntityRegistry): string | null {
  return getDefaultPivotColumn(entity, [])
}

/**
 * Dimension ids reached through a GROUPABLE many-to-many junction (member→group/ministry). They fan rows out, so
 * they're only valid as a group-by in a GROUPED dataset — never on a detail/summary list.
 */
export function getGroupOnlyDimensionIds(entity: ReportEntityRegistry): Set<string> {
  return new Set(
    entity.relationships
      .filter((relationship) => relationship.usage === RelationshipUsage.GROUPABLE)
      .flatMap((relationship) => relationship.allowedDimensions ?? []),
  )
}

function getBaseDimensionIds(entity: ReportEntityRegistry) {
  const relationshipDimensionIds = new Set(
    entity.relationships.flatMap((relationship) => relationship.allowedDimensions ?? []),
  )

  return entity.dimensions
    .map((dimension) => dimension.id)
    .filter((dimensionId) => !relationshipDimensionIds.has(dimensionId))
}

/**
 * A sensible default group-by: prefer a low-cardinality categorical field (status/type/category) over the
 * first column (which is often a unique reference and would make grouping look like a plain detail list).
 */
function getDefaultGroupByIds(entity: ReportEntityRegistry): string[] {
  const baseIds = getBaseDimensionIds(entity)
  const dimensionById = new Map(entity.dimensions.map((dimension) => [dimension.id, dimension]))
  const categorical = baseIds.filter((id) => dimensionById.get(id)?.valueType === ReportValueType.SELECT)
  return (categorical.length > 0 ? categorical : baseIds).slice(0, 1)
}

/** A sensible pivot column axis: prefer a time-buckettable date field (months across the top), else a
 *  categorical field, excluding whatever is already on the row axis. */
function getDefaultPivotColumn(entity: ReportEntityRegistry, excludeIds: string[]): string | null {
  const dimensionById = new Map(entity.dimensions.map((dimension) => [dimension.id, dimension]))
  const candidates = getBaseDimensionIds(entity).filter((id) => !excludeIds.includes(id))
  const dated = candidates.find((id) => (dimensionById.get(id)?.timeGrains?.length ?? 0) > 0)
  if (dated) {
    return dated
  }
  const categorical = candidates.find((id) => dimensionById.get(id)?.valueType === ReportValueType.SELECT)
  return categorical ?? candidates[0] ?? null
}

function getDefaultSelection(entity: ReportEntityRegistry): ReportBuilderSelection {
  const datasetKind = getDefaultDatasetKind(entity)
  const baseDimensions = getBaseDimensionIds(entity)
  const defaultDimensions = datasetKind === ReportDatasetKind.DETAIL ? baseDimensions.slice(0, 4) : []
  const defaultMeasures =
    datasetKind === ReportDatasetKind.SUMMARY ? entity.measures.slice(0, 2).map((measure) => measure.id) : []
  const viewType = getDefaultViewType(entity, datasetKind)

  return {
    entityId: entity.id,
    datasetKind,
    dimensionIds: defaultDimensions,
    measureIds: defaultMeasures,
    relationshipIds: [],
    filters: [],
    timeGrains: [],
    subtotals: false,
    pivotColumn: null,
    sort: null,
    topN: null,
    measureFilters: [],
    viewType,
    chartType: ReportChartType.BAR,
    chartXAxis: null,
    chartYAxis: defaultMeasures.slice(0, 1),
    chartSeriesDimension: null,
  }
}

function normalizeChartYAxis(selection: ReportBuilderSelection) {
  const next = selection.chartYAxis.filter((measureId) => selection.measureIds.includes(measureId))
  return next.length > 0 ? next : selection.measureIds.slice(0, 1)
}

function parseFilterValue(filter: ReportFilter | undefined, draft: ReportFilterDraft): unknown {
  const rawValue = draft.rawValue.trim()
  if (!filter) {
    return rawValue
  }

  // A relative-date filter carries a preset key (e.g. "thisMonth") — pass it through untouched; the planner
  // resolves it to a concrete range. (Never coerce it as a number/date.)
  if (draft.operator === ReportFilterOperator.RELATIVE_DATE) {
    return rawValue
  }

  if (draft.operator === ReportFilterOperator.IN || draft.operator === ReportFilterOperator.NOT_IN) {
    return rawValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  }

  if (draft.operator === ReportFilterOperator.BETWEEN) {
    return rawValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .slice(0, 2)
  }

  switch (filter.valueType) {
    case ReportValueType.NUMBER:
    case ReportValueType.CURRENCY:
    case ReportValueType.PERCENTAGE: {
      const parsed = Number(rawValue)
      return Number.isFinite(parsed) ? parsed : rawValue
    }
    case ReportValueType.BOOLEAN:
      if (rawValue.toLowerCase() === "true") return true
      if (rawValue.toLowerCase() === "false") return false
      return rawValue
    default:
      return rawValue
  }
}

/**
 * The scope a run is executed under.
 *
 * Only a BRANCH run names a branch. PARISH and WORKSPACE must carry NO `branchIds` at all — the server
 * refuses both ("Parish reporting scope must not provide branchIds") because it resolves the system branch
 * itself for parish, and applies no branch predicate at all for workspace. Sending the active branch would
 * be asking for something the caller does not get to choose.
 *
 * This used to exclude only WORKSPACE, which held while the catalog lived under Finance and every run was a
 * branch run. Reports is top level now, so the ORGANIZATION route runs at parish scope — and every engine
 * template there failed validation until parish joined the exclusion.
 */
export function buildReportingScopeDefinition(scope: ReportsRouteScope) {
  return {
    kind: scope.scopeKind,
    workspaceId: scope.workspaceId,
    branchIds: scope.scopeKind === ReportScope.BRANCH ? [scope.branchId] : undefined,
  }
}

/** Operators that assert on the column itself (presence/absence) and carry no comparison value. */
const NO_VALUE_FILTER_OPERATORS = new Set<ReportFilterOperator>([
  ReportFilterOperator.IS_NULL,
  ReportFilterOperator.IS_NOT_NULL,
])

export function isNoValueFilterOperator(operator: ReportFilterOperator): boolean {
  return NO_VALUE_FILTER_OPERATORS.has(operator)
}

/** Whether a filter draft carries enough value to be sent. BETWEEN needs both bounds (e.g. a full date range). */
function isCompleteFilterValue(operator: ReportFilterOperator, rawValue: string): boolean {
  if (isNoValueFilterOperator(operator)) {
    return true
  }
  if (operator === ReportFilterOperator.BETWEEN) {
    return rawValue.split(",").map((part) => part.trim()).filter(Boolean).length === 2
  }
  return rawValue.trim().length > 0
}

export function buildReportingDefinition(params: {
  scope: ReportsRouteScope
  selection: ReportBuilderSelection
  activeEntity: ReportEntityRegistry
  selectedRelationships: string[]
}): ReportDefinition {
  const { scope, selection, activeEntity, selectedRelationships } = params
  const filterMap = new Map(activeEntity.filters.map((filter) => [filter.id, filter]))
  const filters = selection.filters
    .filter(
      (filter) =>
        filter.field.trim().length > 0 &&
        // No-value operators apply without a value; BETWEEN needs both bounds; everything else needs a value.
        isCompleteFilterValue(filter.operator, filter.rawValue),
    )
    .map<ReportFilterClause>((filter) => ({
      field: filter.field,
      operator: filter.operator,
      value: isNoValueFilterOperator(filter.operator)
        ? null
        : parseFilterValue(filterMap.get(filter.field), filter),
    }))

  // DETAIL lists raw rows; GROUPED rolls them up by the chosen dimensions; both carry dimensions + relationships
  // (and pagination). SUMMARY is a single aggregate row (measures only). PIVOT is a grouped dataset whose rows
  // are `dimensionIds`, columns are `pivotColumn`, and values are the measures — described by `dataset.pivot`.
  const carriesDimensions =
    selection.datasetKind === ReportDatasetKind.DETAIL || selection.datasetKind === ReportDatasetKind.GROUPED
  const isGrouped = selection.datasetKind === ReportDatasetKind.GROUPED
  const isPivot = selection.viewType === ReportViewType.PIVOT

  // Grouped-only (junction) dimensions are dropped from non-grouped datasets so we never send a fan-out dimension
  // the planner will reject on a detail/summary list. Their relationship is dropped too (else the junction would
  // still be joined and fan out the rows).
  const groupOnlyDimensionIds = getGroupOnlyDimensionIds(activeEntity)
  const groupOnlyRelationshipIds = new Set(
    activeEntity.relationships
      .filter((relationship) => relationship.usage === RelationshipUsage.GROUPABLE)
      .map((relationship) => relationship.id),
  )
  const usableDimensionIds = isGrouped
    ? selection.dimensionIds
    : selection.dimensionIds.filter((id) => !groupOnlyDimensionIds.has(id))
  const usableRelationships = isGrouped
    ? selectedRelationships
    : selectedRelationships.filter((id) => !groupOnlyRelationshipIds.has(id))

  const pivotColumnIds = isPivot && selection.pivotColumn ? [selection.pivotColumn] : []
  const groupByDimensionIds = carriesDimensions
    ? [...new Set([...usableDimensionIds, ...pivotColumnIds])]
    : []

  // Explicit grains, plus auto-month for any date/datetime group-by dimension that lacks one (so date axes
  // bucket meaningfully — especially the pivot column spread across the top).
  const dimensionById = new Map(activeEntity.dimensions.map((dimension) => [dimension.id, dimension]))
  const explicitGrains = selection.timeGrains.filter((item) => groupByDimensionIds.includes(item.dimension))
  const autoGrains = groupByDimensionIds
    .filter(
      (id) =>
        (dimensionById.get(id)?.timeGrains?.length ?? 0) > 0 &&
        !explicitGrains.some((item) => item.dimension === id),
    )
    .map((id) => ({ dimension: id, grain: ReportTimeGrain.MONTH }))
  const timeGrains = [...explicitGrains, ...autoGrains]

  // Server-side sort, only emitted when its field is still a valid choice for the current dataset: a selected
  // sortable dimension (detail/grouped), or a selected measure (grouped only). Otherwise omit so the planner
  // falls back to the registry default instead of rejecting a stale sort field.
  const sortableDimensionIds = new Set(
    activeEntity.dimensions
      .filter((dimension) => selection.dimensionIds.includes(dimension.id) && dimension.sortable)
      .map((dimension) => dimension.id),
  )
  // Every measure (including resolver / relationship-aggregate) is usable in grouped datasets now — the backend
  // computes those per-group and merges them by group key — so no measure filtering is needed here.
  const datasetMeasures = selection.measureIds
  const chartYAxis = selection.chartYAxis.length > 0 ? selection.chartYAxis : selection.measureIds

  // Measure (HAVING) filters apply only to grouped reports, only to selected measures, and only when complete.
  const datasetMeasureIdSet = new Set(datasetMeasures)
  const measureFilters = isGrouped
    ? selection.measureFilters
        .filter((draft) => datasetMeasureIdSet.has(draft.measure) && draft.rawValue.trim().length > 0 && Number.isFinite(Number(draft.rawValue)))
        .map((draft) => ({ measure: draft.measure, operator: draft.operator, value: Number(draft.rawValue) }))
    : []

  const sortMeasureIds = isGrouped ? new Set(datasetMeasures) : new Set<string>()
  const sortValid =
    carriesDimensions &&
    selection.sort != null &&
    (sortableDimensionIds.has(selection.sort.field) || sortMeasureIds.has(selection.sort.field))
  const sort = sortValid && selection.sort ? [selection.sort] : undefined

  return {
    version: 1,
    baseEntity: activeEntity.id,
    scope: buildReportingScopeDefinition(scope),
    dataset: {
      kind: selection.datasetKind,
      dimensions: groupByDimensionIds,
      measures: datasetMeasures,
      relationships: carriesDimensions ? usableRelationships : [],
      filters,
      sort,
      timeGrains: isGrouped && timeGrains.length > 0 ? timeGrains : undefined,
      measureFilters: measureFilters.length > 0 ? measureFilters : undefined,
      // Subtotals/grouping are computed client-side by the DataTable (one grouping engine across the app), so the
      // server returns flat leaf rows (one per dimension combination) — never request a server-side ROLLUP here,
      // or the client would re-aggregate the rollup rows and double-count.
      grouping: undefined,
      pivot:
        isPivot && selection.pivotColumn
          ? { rows: selection.dimensionIds, columns: pivotColumnIds, values: selection.measureIds }
          : undefined,
      // Grouped reports default to 100 groups; a Top-N cap (applied after the sort) narrows it to the leaders.
      pagination: carriesDimensions
        ? {
            limit: isGrouped
              ? Math.min(100, Math.max(1, selection.topN ?? 100))
              : 25,
          }
        : undefined,
    },
    view: {
      type: selection.viewType,
      chart:
        selection.viewType === ReportViewType.CHART
          ? {
              chartType: selection.chartType,
              // The chart groups by this dimension (x-axis) and plots one series per measure (y-axis), or — when a
              // breakdown dimension is set — one series per distinct value of it (for the first measure).
              xAxis: selection.chartXAxis ?? undefined,
              yAxis: chartYAxis,
              series: selection.chartSeriesDimension ?? undefined,
            }
          : undefined,
    },
  }
}

/** Map a stored/resolved {@link ReportDefinition} into an editable builder selection (saved reports + templates). */
export function selectionFromDefinition(definition: ReportDefinition): ReportBuilderSelection {
  const pivotDefinition = definition.dataset.pivot
  const dimensionIds = pivotDefinition ? pivotDefinition.rows : definition.dataset.dimensions
  const measureIds = pivotDefinition ? pivotDefinition.values : definition.dataset.measures

  return {
    entityId: definition.baseEntity,
    datasetKind: definition.dataset.kind,
    dimensionIds,
    measureIds,
    relationshipIds: definition.dataset.relationships,
    filters: definition.dataset.filters.map((filter) => ({
      id: createFilterDraft().id,
      field: filter.field,
      operator: filter.operator,
      rawValue: Array.isArray(filter.value) ? filter.value.join(", ") : String(filter.value ?? ""),
    })),
    timeGrains: definition.dataset.timeGrains ?? [],
    subtotals: definition.dataset.grouping?.subtotals ?? false,
    pivotColumn: pivotDefinition?.columns[0] ?? null,
    sort: definition.dataset.sort?.[0] ?? null,
    measureFilters: (definition.dataset.measureFilters ?? []).map((clause) => ({
      id: createFilterDraft().id,
      measure: clause.measure,
      operator: clause.operator,
      rawValue: String(clause.value),
    })),
    // A grouped report saved with a non-default page limit was a Top-N cap; restore it (else it's "no cap").
    topN:
      definition.dataset.kind === ReportDatasetKind.GROUPED &&
      definition.dataset.pagination?.limit != null &&
      definition.dataset.pagination.limit < 100
        ? definition.dataset.pagination.limit
        : null,
    viewType: definition.view.type,
    chartType: definition.view.chart?.chartType ?? ReportChartType.BAR,
    chartXAxis: definition.view.chart?.xAxis ?? null,
    chartYAxis: definition.view.chart?.yAxis ?? definition.dataset.measures.slice(0, 1),
    chartSeriesDimension: definition.view.chart?.series ?? null,
  }
}

export const reportBuilderTestUtils = {
  createFilterDraft,
  getDefaultSelection,
  parseFilterValue,
  normalizeChartYAxis,
  buildReportingScopeDefinition,
  buildReportingDefinition,
  seedDateWindowFilter,
}

export function useReportBuilder(scope: ReportsRouteScope | null, entities: ReportEntityRegistry[]) {
  const entityMap = React.useMemo(() => new Map(entities.map((entity) => [entity.id, entity])), [entities])

  const [selection, setSelection] = React.useState<ReportBuilderSelection | null>(null)
  const [activeSavedReportId, setActiveSavedReportId] = React.useState<string | null>(null)
  const [activeSavedReportName, setActiveSavedReportName] = React.useState<string | null>(null)
  const [activeSavedReportDescription, setActiveSavedReportDescription] = React.useState<string | null>(null)
  const [activeSavedReportVisibility, setActiveSavedReportVisibility] = React.useState<ReportVisibility>(
    ReportVisibility.PRIVATE,
  )
  // Owner of the loaded report — used to gate "Save changes" (only the owner can update in place).
  const [activeSavedReportOwnerId, setActiveSavedReportOwnerId] = React.useState<string | null>(null)
  // True once the loaded report has unsaved edits. The loaded identity (id/name) is kept across edits so the
  // builder can offer "Save changes" — only `markDirty()` flips, not the id.
  const [isDirty, setIsDirty] = React.useState(false)
  const markDirty = React.useCallback(() => setIsDirty(true), [])

  const activeEntity = React.useMemo(
    () => (selection?.entityId ? entityMap.get(selection.entityId) ?? null : null),
    [entityMap, selection?.entityId],
  )

  const relationshipFieldGroups = React.useMemo<RelationshipFieldGroup[]>(() => {
    if (!activeEntity) {
      return []
    }

    return activeEntity.relationships
      .map((relationship) => ({
        relationship,
        dimensions: activeEntity.dimensions.filter((dimension) =>
          (relationship.allowedDimensions ?? []).includes(dimension.id),
        ),
      }))
      .filter((group) => group.dimensions.length > 0)
  }, [activeEntity])

  const relationshipDimensionIdSet = React.useMemo(
    () => new Set(relationshipFieldGroups.flatMap((group) => group.dimensions.map((dimension) => dimension.id))),
    [relationshipFieldGroups],
  )

  const baseDimensions = React.useMemo(
    () => activeEntity?.dimensions.filter((dimension) => !relationshipDimensionIdSet.has(dimension.id)) ?? [],
    [activeEntity, relationshipDimensionIdSet],
  )

  const selectedRelationships = React.useMemo(() => {
    if (!selection) {
      return []
    }

    return relationshipFieldGroups
      .filter((group) => group.dimensions.some((dimension) => selection.dimensionIds.includes(dimension.id)))
      .map((group) => group.relationship.id)
  }, [relationshipFieldGroups, selection])

  const availableViews = React.useMemo(() => {
    if (!activeEntity || !selection) {
      return []
    }

    // The chosen view drives the dataset kind, so offer every view the entity supports.
    // PIVOT and PRINTABLE are deferred for V1 — kept in the engine, hidden from the builder.
    return activeEntity.capabilities.views.filter(
      (viewType) => viewType !== ReportViewType.PRINTABLE && viewType !== ReportViewType.PIVOT,
    )
  }, [activeEntity, selection])

  const availableExports = React.useMemo(() => activeEntity?.capabilities.exports ?? [], [activeEntity])

  const applyEntity = React.useCallback((entityId: string) => {
    const entity = entityMap.get(entityId)
    if (!entity) {
      return
    }

    setSelection(getDefaultSelection(entity))
    setActiveSavedReportId(null)
    setActiveSavedReportName(null)
    setActiveSavedReportDescription(null)
    setActiveSavedReportVisibility(ReportVisibility.PRIVATE)
    setActiveSavedReportOwnerId(null)
    setIsDirty(false)
  }, [entityMap])

  // Selecting a view also sets the dataset kind it needs and ensures the required fields exist, so every view
  // produces a valid definition (e.g. Chart/Summary get a default measure; Grouped/Pivot get group-by dims).
  const setViewType = React.useCallback((viewType: ReportViewType) => {
    setSelection((current) => {
      if (!current || !activeEntity) {
        return current
      }

      const datasetKind = datasetKindForView(viewType)
      const carriesDimensions =
        datasetKind === ReportDatasetKind.DETAIL || datasetKind === ReportDatasetKind.GROUPED
      const needsMeasures =
        datasetKind === ReportDatasetKind.SUMMARY ||
        datasetKind === ReportDatasetKind.GROUPED ||
        viewType === ReportViewType.CHART

      let dimensionIds = carriesDimensions ? current.dimensionIds : []
      if (datasetKind === ReportDatasetKind.DETAIL && dimensionIds.length === 0) {
        dimensionIds = getBaseDimensionIds(activeEntity).slice(0, 4)
      }
      if (datasetKind === ReportDatasetKind.GROUPED && dimensionIds.length === 0) {
        dimensionIds = getDefaultGroupByIds(activeEntity)
      }

      const measureIds =
        needsMeasures && current.measureIds.length === 0
          ? activeEntity.measures.slice(0, 2).map((measure) => measure.id)
          : current.measureIds

      const isPivot = viewType === ReportViewType.PIVOT
      const pivotColumn = isPivot
        ? current.pivotColumn && !dimensionIds.includes(current.pivotColumn)
          ? current.pivotColumn
          : getDefaultPivotColumn(activeEntity, dimensionIds)
        : null

      // A chart groups by a single x-axis dimension; keep/seed a valid one and mirror it into dimensionIds.
      let chartXAxis = current.chartXAxis
      // Preserve a still-valid breakdown series when (re-)entering the chart view; otherwise drop it.
      let chartSeriesDimension = current.chartSeriesDimension
      if (viewType === ReportViewType.CHART) {
        const baseDimensionIds = getBaseDimensionIds(activeEntity)
        chartXAxis =
          current.chartXAxis && baseDimensionIds.includes(current.chartXAxis)
            ? current.chartXAxis
            : getDefaultChartXAxis(activeEntity)
        const seriesIsValid =
          chartSeriesDimension != null &&
          chartSeriesDimension !== chartXAxis &&
          activeEntity.dimensions.some((dimension) => dimension.id === chartSeriesDimension)
        chartSeriesDimension = seriesIsValid ? chartSeriesDimension : null
        dimensionIds = chartXAxis
          ? [chartXAxis, ...(chartSeriesDimension ? [chartSeriesDimension] : [])]
          : []
      } else {
        chartSeriesDimension = null
      }

      const nextSelection: ReportBuilderSelection = {
        ...current,
        datasetKind,
        viewType,
        dimensionIds,
        relationshipIds: carriesDimensions ? current.relationshipIds : [],
        measureIds,
        timeGrains: datasetKind === ReportDatasetKind.GROUPED ? current.timeGrains : [],
        subtotals: datasetKind === ReportDatasetKind.GROUPED ? current.subtotals : false,
        pivotColumn,
        chartXAxis,
        chartSeriesDimension,
      }
      nextSelection.chartYAxis = normalizeChartYAxis(nextSelection)
      return nextSelection
    })
    markDirty()
  }, [activeEntity, markDirty])

  const setChartType = React.useCallback((chartType: ReportChartType) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        chartType,
      }
    })
    markDirty()
  }, [markDirty])

  const setDatasetKind = React.useCallback((datasetKind: ReportDatasetKind) => {
    setSelection((current) => {
      if (!current || !activeEntity) {
        return current
      }

      const carriesDimensions =
        datasetKind === ReportDatasetKind.DETAIL || datasetKind === ReportDatasetKind.GROUPED
      const needsMeasures =
        datasetKind === ReportDatasetKind.SUMMARY || datasetKind === ReportDatasetKind.GROUPED
      const nextViewType = getDefaultViewType(activeEntity, datasetKind)

      const nextSelection: ReportBuilderSelection = {
        ...current,
        datasetKind,
        dimensionIds: carriesDimensions ? current.dimensionIds : [],
        relationshipIds: carriesDimensions ? current.relationshipIds : [],
        measureIds:
          needsMeasures && current.measureIds.length === 0
            ? activeEntity.measures.slice(0, 2).map((measure) => measure.id)
            : current.measureIds,
        timeGrains: datasetKind === ReportDatasetKind.GROUPED ? current.timeGrains : [],
        subtotals: datasetKind === ReportDatasetKind.GROUPED ? current.subtotals : false,
        viewType: nextViewType,
      }

      nextSelection.chartYAxis = normalizeChartYAxis(nextSelection)
      return nextSelection
    })
    markDirty()
  }, [activeEntity, markDirty])

  const toggleDimension = React.useCallback((dimensionId: string, checked: boolean) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      const nextDimensions = checked
        ? [...new Set([...current.dimensionIds, dimensionId])]
        : current.dimensionIds.filter((value) => value !== dimensionId)

      // Keep time grains in sync with the selected group-by dimensions.
      let nextTimeGrains = current.timeGrains.filter((selectionItem) => nextDimensions.includes(selectionItem.dimension))
      let nextFilters = current.filters

      // When grouping by a date/datetime field, default it to monthly buckets so the grouping is meaningful
      // (grouping by an exact timestamp would yield one row each — looking like a detail list), and seed a
      // last-12-months window so the result stays fast + non-empty by default.
      if (checked && current.datasetKind === ReportDatasetKind.GROUPED) {
        const dimension = activeEntity?.dimensions.find((candidate) => candidate.id === dimensionId)
        if (dimension?.timeGrains?.length && !nextTimeGrains.some((item) => item.dimension === dimensionId)) {
          nextTimeGrains = [...nextTimeGrains, { dimension: dimensionId, grain: ReportTimeGrain.MONTH }]
          nextFilters = seedDateWindowFilter(nextFilters, activeEntity, dimensionId)
        }
      }

      return {
        ...current,
        dimensionIds: nextDimensions,
        timeGrains: nextTimeGrains,
        filters: nextFilters,
      }
    })
    markDirty()
  }, [activeEntity, markDirty])

  const toggleMeasure = React.useCallback((measureId: string, checked: boolean) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      const nextMeasures = checked
        ? [...new Set([...current.measureIds, measureId])]
        : current.measureIds.filter((value) => value !== measureId)

      const nextSelection = {
        ...current,
        measureIds: nextMeasures,
      }

      nextSelection.chartYAxis = normalizeChartYAxis(nextSelection)
      return nextSelection
    })
    markDirty()
  }, [markDirty])

  const toggleChartMeasure = React.useCallback((measureId: string, checked: boolean) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      const nextChartYAxis = checked
        ? [...new Set([...current.chartYAxis, measureId])]
        : current.chartYAxis.filter((value) => value !== measureId)

      return {
        ...current,
        chartYAxis: nextChartYAxis.length > 0 ? nextChartYAxis : current.measureIds.slice(0, 1),
      }
    })
    markDirty()
  }, [markDirty])

  const addFilter = React.useCallback(() => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        filters: [...current.filters, createFilterDraft(activeEntity?.filters[0])],
      }
    })
    markDirty()
  }, [activeEntity?.filters, markDirty])

  const updateFilter = React.useCallback((filterId: string, patch: Partial<ReportFilterDraft>) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        filters: current.filters.map((filter) => {
          if (filter.id !== filterId) {
            return filter
          }

          const nextField = patch.field ?? filter.field
          const matchedFilter = activeEntity?.filters.find((candidate) => candidate.id === nextField)

          return {
            ...filter,
            ...patch,
            operator:
              patch.field && matchedFilter
                ? matchedFilter.operators[0] ?? patch.operator ?? filter.operator
                : patch.operator ?? filter.operator,
            // Changing the field resets the value — its meaning (date / id / enum / text) differs per filter.
            rawValue: patch.field ? "" : patch.rawValue ?? filter.rawValue,
          }
        }),
      }
    })
    markDirty()
  }, [activeEntity?.filters, markDirty])

  const removeFilter = React.useCallback((filterId: string) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        filters: current.filters.filter((filter) => filter.id !== filterId),
      }
    })
    markDirty()
  }, [markDirty])

  const setTimeGrain = React.useCallback((dimensionId: string, grain: ReportTimeGrain | null) => {
    setSelection((current) => {
      if (!current) {
        return current
      }

      const others = current.timeGrains.filter((selectionItem) => selectionItem.dimension !== dimensionId)
      return {
        ...current,
        timeGrains: grain ? [...others, { dimension: dimensionId, grain }] : others,
      }
    })
    markDirty()
  }, [markDirty])

  const setSubtotals = React.useCallback((subtotals: boolean) => {
    setSelection((current) => (current ? { ...current, subtotals } : current))
    markDirty()
  }, [markDirty])

  const setPivotColumn = React.useCallback((pivotColumn: string | null) => {
    setSelection((current) => (current ? { ...current, pivotColumn } : current))
    markDirty()
  }, [markDirty])

  const setSort = React.useCallback((sort: ReportSort | null) => {
    setSelection((current) => (current ? { ...current, sort } : current))
    markDirty()
  }, [markDirty])

  const setTopN = React.useCallback((topN: number | null) => {
    setSelection((current) => (current ? { ...current, topN } : current))
    markDirty()
  }, [markDirty])

  const addMeasureFilter = React.useCallback(() => {
    setSelection((current) => {
      if (!current) {
        return current
      }
      const firstMeasure = current.measureIds[0]
      return { ...current, measureFilters: [...current.measureFilters, createMeasureFilterDraft(firstMeasure)] }
    })
    markDirty()
  }, [markDirty])

  const updateMeasureFilter = React.useCallback((id: string, patch: Partial<ReportMeasureFilterDraft>) => {
    setSelection((current) =>
      current
        ? {
            ...current,
            measureFilters: current.measureFilters.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)),
          }
        : current,
    )
    markDirty()
  }, [markDirty])

  const removeMeasureFilter = React.useCallback((id: string) => {
    setSelection((current) =>
      current ? { ...current, measureFilters: current.measureFilters.filter((draft) => draft.id !== id) } : current,
    )
    markDirty()
  }, [markDirty])

  const setChartXAxis = React.useCallback((chartXAxis: string) => {
    // The chart groups by its x-axis dimension (plus an optional breakdown series); mirror both into dimensionIds.
    // Date axes auto-bucket by month in buildReportingDefinition, so clear stale grains here.
    setSelection((current) => {
      if (!current) {
        return current
      }
      const series = current.chartSeriesDimension && current.chartSeriesDimension !== chartXAxis
        ? [current.chartSeriesDimension]
        : []
      const dimension = activeEntity?.dimensions.find((candidate) => candidate.id === chartXAxis)
      const nextFilters = dimension?.timeGrains?.length
        ? seedDateWindowFilter(current.filters, activeEntity, chartXAxis)
        : current.filters
      return { ...current, chartXAxis, dimensionIds: [chartXAxis, ...series], timeGrains: [], filters: nextFilters }
    })
    markDirty()
  }, [activeEntity, markDirty])

  // CHART breakdown: one series per distinct value of this dimension (single measure). Mirror it into dimensionIds
  // alongside the x-axis so the grouped query returns [x, series] combinations for the client to pivot.
  const setChartSeries = React.useCallback((chartSeriesDimension: string | null) => {
    setSelection((current) => {
      if (!current) {
        return current
      }
      const xAxis = current.chartXAxis
      const dimensionIds = xAxis
        ? [xAxis, ...(chartSeriesDimension && chartSeriesDimension !== xAxis ? [chartSeriesDimension] : [])]
        : current.dimensionIds
      return { ...current, chartSeriesDimension, dimensionIds }
    })
    markDirty()
  }, [markDirty])

  const buildDefinition = React.useCallback((): ReportDefinition | null => {
    if (!scope || !selection || !activeEntity) {
      return null
    }

    return buildReportingDefinition({
      scope,
      selection,
      activeEntity,
      selectedRelationships,
    })
  }, [activeEntity, scope, selectedRelationships, selection])

  const loadSavedReport = React.useCallback((report: ReportingSavedReportListResult["items"][number]) => {
    if (!entityMap.has(report.definition.baseEntity)) {
      return
    }

    setSelection(selectionFromDefinition(report.definition))
    setActiveSavedReportId(report.id)
    setActiveSavedReportName(report.name)
    setActiveSavedReportDescription(report.description ?? null)
    setActiveSavedReportVisibility(report.visibility ?? ReportVisibility.PRIVATE)
    setActiveSavedReportOwnerId(report.createdByUserId ?? null)
    setIsDirty(false)
  }, [entityMap])

  // Open a resolved template (or any definition) as a fresh, editable draft — no saved-report identity yet.
  const loadDefinition = React.useCallback((definition: ReportDefinition) => {
    if (!entityMap.has(definition.baseEntity)) {
      return
    }

    setSelection(selectionFromDefinition(definition))
    setActiveSavedReportId(null)
    setActiveSavedReportName(definition.name ?? null)
    setActiveSavedReportDescription(null)
    setActiveSavedReportVisibility(ReportVisibility.PRIVATE)
    setActiveSavedReportOwnerId(null)
    setIsDirty(true)
  }, [entityMap])

  return {
    selection,
    activeEntity,
    baseDimensions,
    relationshipFieldGroups,
    availableViews,
    availableExports,
    activeSavedReportId,
    activeSavedReportName,
    activeSavedReportDescription,
    activeSavedReportVisibility,
    activeSavedReportOwnerId,
    isDirty,
    applyEntity,
    setDatasetKind,
    setViewType,
    setChartType,
    toggleDimension,
    toggleMeasure,
    toggleChartMeasure,
    addFilter,
    updateFilter,
    removeFilter,
    setTimeGrain,
    setSubtotals,
    setPivotColumn,
    setSort,
    setTopN,
    addMeasureFilter,
    updateMeasureFilter,
    removeMeasureFilter,
    setChartXAxis,
    setChartSeries,
    buildDefinition,
    loadSavedReport,
    loadDefinition,
    setActiveSavedReportId,
    setActiveSavedReportName,
    setActiveSavedReportDescription,
    setActiveSavedReportVisibility,
    setActiveSavedReportOwnerId,
    setIsDirty,
  }
}
